// components/EventHeader/EventDetailHeader.js

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../Button/Button';
import Typography from '../Typography';
import OrganizerInfo from './OrganizerInfo';
import Tag from '../Tag';
import ClipboardButton from '../ClipboardButton/ClipboardButton';
import ChevronLeftIcon from '../Icons/ChevronLeftIcon';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import ArrowRightIcon from '../Icons/ArrowRightIcon';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import BackIcon from '../Icons/BackIcon';
import GearIcon from '../Icons/GearIcon';
import { isEventPast } from '../../utils/dateUtils';

const EventHeader = ({
  title,
  availableSeats,
  eventType,
  terrain,
  isOwner,
  manageLink,
  isEnrolled,
  handleEnroll,
  eventId,
  organizer,
  copyUrl,
  disabled,
  eventHasPassed,
  onViewEnrollment,
  status,
  endDate,
}) => {
  const { t: tCategories } = useTranslation('eventCategories');
  const { t: tEventDetail } = useTranslation('eventDetail');
  const { width } = useWindowDimensions();
  const isMobile = width < parseInt(theme.breakpoints.md);

  return (
    <HeaderContainer>
      <Container>
        <UpperActions>
          <BackButton
            size="l"
            sizeTablet="l"
            sizeMobile="m"
            onClick={() => window.history.back()} $variant="ghost">
            <BackIcon height={10} width={10} fill={theme.colors.defaultStrong} />
            {tEventDetail('eventDetailHeader.backButton')}
          </BackButton>
          <ActionsContainer>
            <ClipboardButton
              text={copyUrl}
              $variant="ghost"
              label={tEventDetail('eventDetailHeader.shareButton')}
              aria-label={tEventDetail('eventDetailHeader.shareButton')}
              tooltipText={tEventDetail('eventDetailHeader.copiedUrl')}
            // label={tEventDetail('eventDetailHeader.shareButton')}
            />

            {isOwner ? (
              <Link to={manageLink}>
                <Button size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                  $variant="outline"
                  aria-label={tEventDetail('eventDetailHeader.manageEventButton')}>
                  <GearIcon height={16} width={16} fill={theme.colors.defaultStrong} />
                  {tEventDetail('eventDetailHeader.manageEventButton')}
                  {/* <ArrowRightIcon fill={theme.colors.defaultStrong} /> */}
                </Button>
              </Link>
            ) : isEnrolled ? (
              <Button
                onClick={onViewEnrollment}
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                $variant="outline"
                aria-label={tEventDetail('eventDetailHeader.viewEnrollment')}>
                {tEventDetail('eventDetailHeader.viewEnrollment')}
                <ArrowRightIcon fill={theme.colors.defaultStrong} />
              </Button>
            ) : !eventHasPassed && (
              <Button
                onClick={handleEnroll}
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                $variant="outline"
                disabled={isEnrolled || isOwner || disabled}
                aria-label={tEventDetail('eventDetailHeader.enrollButton')}
              >
                {tEventDetail('eventDetailHeader.enrollButton')}
              </Button>
            )}
          </ActionsContainer>
        </UpperActions>
        <HeaderMain>
          <TitleContainer>
            <EventTitle $variant="title-2-semibold" $variantTablet="title-3-semibold" $variantMobile="title-4-semibold" as="h1">{title}</EventTitle>
            <TagsWrapper>
              {status === 'cancelled' && <Tag $variant="rejected">{tEventDetail('eventDetailHeader.cancelled')}</Tag>}
              {endDate && isEventPast(endDate) ? (
                <Tag $variant="subtle">{tEventDetail('eventDetailHeader.finished')}</Tag>
              ) : (
                <>
                  {isEnrolled && <Tag $variant="brand">{tEventDetail('eventDetailHeader.enrolled')}</Tag>}
                </>
              )}


              <Tag $variant="transparent">{availableSeats} {tEventDetail('eventDetailHeader.seats')}</Tag>
              <Tag $variant='transparent'>{tCategories(`type.${eventType}`)}</Tag>

              {Array.isArray(terrain) ? (
                terrain.map((tItem, index) => (
                  <Tag $variant='transparent' key={index}>{tCategories(`terrain.${tItem}`)}</Tag>
                ))
              ) : (
                <Tag $variant='transparent'>{tCategories(`terrain.${terrain}`)}</Tag>
              )}
            </TagsWrapper>
          </TitleContainer>
        </HeaderMain>
        <OrganizerContainer>
          <OrganizerInfo organizer={organizer} />
        </OrganizerContainer>
      </Container>
    </HeaderContainer >
  );
};

export default EventHeader;

// Styled Components
const HeaderContainer = styled.section`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  }
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  row-gap: ${({ theme }) => theme.sizing.sm};
`;

const TitleContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
  
  // @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
  //   row-gap: ${({ theme }) => theme.sizing.xs};
  // }
`;

const EventTitle = styled(Typography)`
  flex-shrink: 0;
`;

const TagsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  gap: ${({ theme }) => theme.sizing.xxs};
  flex-wrap: wrap;
`;

const ActionsContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const OrganizerContainer = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-top: ${({ theme }) => theme.sizing.xs};
`;

const UpperActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const BackButton = styled(Button)`
  // gap: 0;
  margin-left: -10px;
  // padding: ${({ theme }) => theme.sizing.xxxs};
  // padding-right: ${({ theme }) => theme.sizing.xs};
`;